import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail, setToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, ArrowLeft } from "lucide-react";

export default function Auth() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login"
        ? { email: form.email, password: form.password }
        : { email: form.email, password: form.password, name: form.name };
      const { data } = await api.post(path, payload);
      setToken(data.token);
      setUser(data.user);
      toast.success(mode === "login" ? "Welcome back!" : "Account created!");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-ink text-white flex flex-col font-sans">
      <div className="p-6">
        <button onClick={() => navigate("/")} data-testid="back-home-btn" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back home
        </button>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 pb-16">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 justify-center mb-8">
            <Building2 className="h-6 w-6 text-champagne" />
            <span className="font-display text-2xl">Penhaus<span className="text-champagne">AI</span></span>
          </div>
          <div className="bg-ink-surface border border-hairline rounded-lg p-8">
            <h1 className="font-display text-3xl text-center">{mode === "login" ? "Welcome back" : "Create your account"}</h1>
            <p className="text-zinc-400 text-center mt-2 text-sm">{mode === "login" ? "Sign in to write your next listing." : "Start free — no credit card required."}</p>

            <Button onClick={googleLogin} data-testid="google-login-btn" variant="outline" className="w-full mt-7 h-11 rounded-sm bg-white text-black hover:bg-zinc-100 border-0 font-medium">
              <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z"/></svg>
              Continue with Google
            </Button>

            <div className="flex items-center gap-3 my-6">
              <div className="h-px flex-1 bg-white/10" />
              <span className="text-xs text-zinc-500 uppercase tracking-wider">or</span>
              <div className="h-px flex-1 bg-white/10" />
            </div>

            <form onSubmit={submit} className="space-y-4">
              {mode === "register" && (
                <div>
                  <Label className="text-zinc-300">Full name</Label>
                  <Input data-testid="auth-name-input" value={form.name} onChange={update("name")} required placeholder="Jane Agent"
                    className="mt-1.5 bg-black/30 border-white/10 text-white placeholder:text-zinc-600 h-11" />
                </div>
              )}
              <div>
                <Label className="text-zinc-300">Email</Label>
                <Input data-testid="auth-email-input" type="email" value={form.email} onChange={update("email")} required placeholder="you@brokerage.com"
                  className="mt-1.5 bg-black/30 border-white/10 text-white placeholder:text-zinc-600 h-11" />
              </div>
              <div>
                <Label className="text-zinc-300">Password</Label>
                <Input data-testid="auth-password-input" type="password" value={form.password} onChange={update("password")} required placeholder="••••••••"
                  className="mt-1.5 bg-black/30 border-white/10 text-white placeholder:text-zinc-600 h-11" />
              </div>
              <Button type="submit" disabled={loading} data-testid="auth-submit-btn"
                className="w-full h-11 bg-champagne text-black hover:bg-[#F3E5AB] rounded-sm font-semibold">
                {loading ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
              </Button>
            </form>

            <p className="text-center text-sm text-zinc-400 mt-6">
              {mode === "login" ? "New to PenhausAI?" : "Already have an account?"}{" "}
              <button data-testid="auth-toggle-btn" onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-champagne hover:underline font-medium">
                {mode === "login" ? "Create an account" : "Sign in"}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
